import type { DuplicateKeyPersonMatch } from '@commenda/api';
import {
  Avatar,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@commenda/kakashi';
import { formatIsoDateShort } from '@commenda/utils';

export const DuplicateKeyPersonDialog = ({
  isOpen,
  onClose,
  name,
  matches,
  onUseExisting,
  onCreateAnyway,
  isCreating,
}: {
  isOpen: boolean;
  onClose: () => void;
  name: string;
  matches: DuplicateKeyPersonMatch[];
  onUseExisting: (match: DuplicateKeyPersonMatch) => void;
  onCreateAnyway: () => void;
  isCreating: boolean;
}) => (
  <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
    <DialogContent className="max-w-[480px]">
      <DialogHeader>
        <DialogTitle>{name} is already a key person</DialogTitle>
      </DialogHeader>

      <div className="flex flex-col gap-2xl p-3xl">
        <p className="txt-small-regular text-text-secondary">
          {matches.length === 1
            ? 'Someone with the same name and date of birth is already on file. Using their existing record keeps every role in one place.'
            : 'People with the same name and date of birth are already on file. Using an existing record keeps every role in one place.'}
        </p>

        <div className="flex flex-col gap-lg">
          {matches.map((match) => {
            const birthDate = formatIsoDateShort(match.dateOfBirth);
            return (
              <div
                key={match.id}
                className="flex items-center gap-xl rounded-lg border-[0.5px] border-border-primary bg-bg-secondary p-xl"
              >
                <Avatar name={match.name} colorSeed={match.id} size="md" />

                <div className="flex min-w-0 flex-1 flex-col gap-xs">
                  <span className="txt-small-medium truncate text-text-primary">
                    {match.name}
                  </span>
                  <span
                    className="txt-extra-small-regular truncate text-text-secondary"
                    title={match.email ?? undefined}
                  >
                    {match.email ?? 'No email on file'}
                  </span>
                  {birthDate !== null && (
                    <span className="txt-extra-small-regular text-text-tertiary">
                      Born {birthDate}
                    </span>
                  )}
                </div>

                <Button
                  variant="primary"
                  size="sm"
                  className="shrink-0"
                  disabled={isCreating}
                  onClick={() => onUseExisting(match)}
                >
                  Use existing
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      <DialogFooter className="items-center">
        <Button variant="tertiary" onClick={onClose} disabled={isCreating}>
          Cancel
        </Button>
        <Button
          variant="secondary"
          onClick={onCreateAnyway}
          isLoading={isCreating}
          disabled={isCreating}
        >
          Create a new key person
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
